"use strict";

import { CircularProgress } from "@mui/material";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import React from "react";

import classes from "../../Assets/Styles/CharacterVoicePanel.module.css";

const plural = (count, word) => `${count} ${count === 1 ? word : `${word}s`}`;

/**
 * Offers to redo all of a character's lines with a voice that was just added,
 * then follows the server-side run.
 *
 * `offer` is the local step before a run ({ status: "offer" | "counting" |
 * "confirm" | "empty" | "countError" | "starting", total, emotion }); `job` is
 * the run from the server. A job takes precedence once started.
 */
const RedoLinesCard = ({
  name,
  offer,
  job,
  onPrepare,
  onStart,
  onStop,
  onRetryFailed,
  onStartAgain,
  onDismiss,
}) => {
  const status = job ? job.status : offer?.status;
  const total = job?.total ?? offer?.total ?? 0;
  const succeeded = job?.succeeded || 0;
  const failedCount = job?.failed || 0;
  const processed = succeeded + failedCount;
  const percent = total ? Math.round((processed / total) * 100) : 0;
  const voice = offer?.emotion || job?.emotion;

  const button = (label, onClick, primary = false, disabled = false) => (
    <button
      type="button"
      className={classes[primary ? "RedoPrimary" : "RedoSecondary"]}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  let icon = <AutorenewRoundedIcon sx={{ fontSize: 18 }} />;
  let tone = "";
  let title;
  let body = null;
  let actions = null;
  let canDismiss = true;

  switch (status) {
    case "offer":
      title = `Use this voice for all of ${name}'s lines?`;
      body = `Redo every line of ${name} with the ${
        voice || "new"
      } voice you just added.`;
      actions = (
        <>
          {button("Redo all lines", onPrepare, true)}
          {button("Not now", onDismiss)}
        </>
      );
      break;

    case "counting":
      icon = <CircularProgress size={16} color="inherit" />;
      title = `Finding ${name}'s lines…`;
      canDismiss = false;
      break;

    case "confirm":
    case "starting":
      title = `Redo ${plural(total, "line")}?`;
      body =
        "Each line gets a new take with this voice. Its current take stays in the line's take history. This runs on the server, so you can keep working or close the page.";
      actions = (
        <>
          {button(
            status === "starting"
              ? "Starting…"
              : `Redo ${plural(total, "line")}`,
            onStart,
            true,
            status === "starting"
          )}
          {button("Cancel", onDismiss, false, status === "starting")}
        </>
      );
      canDismiss = status !== "starting";
      break;

    case "empty":
      title = `${name} has no voiced lines to redo`;
      body =
        "Lines without a translation, subtitle-only lines and sound effects are skipped.";
      break;

    case "countError":
      icon = <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />;
      tone = classes["RedoCardWarning"];
      title = `Couldn't load ${name}'s lines`;
      body = offer?.message || null;
      actions = button("Try again", onPrepare, true);
      break;

    case "queued":
    case "inProgress":
      canDismiss = false;
      title = job.cancelRequested ? "Stopping…" : `Redoing ${name}'s lines…`;
      body = (
        <>
          <div
            className={`${classes["RedoProgress"]} ${
              status === "queued" ? classes["RedoProgressPending"] : ""
            }`}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={total || undefined}
            aria-valuenow={status === "queued" ? undefined : processed}
            aria-label={`Redoing ${name}'s lines`}
          >
            <span
              style={status === "queued" ? undefined : { width: `${percent}%` }}
            />
          </div>
          <span className={classes["RedoCount"]}>
            {status === "queued"
              ? "Starting on the server…"
              : `${processed} of ${total}`}
            {failedCount > 0 && ` · ${failedCount} failed`}
          </span>
        </>
      );
      actions = job.cancelRequested ? null : button("Stop", onStop);
      break;

    case "completed":
      if (failedCount) {
        icon = <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />;
        tone = classes["RedoCardWarning"];
        title = `Redid ${succeeded} of ${plural(total, "line")}`;
        body = `${plural(failedCount, "line")} couldn't be generated.`;
        actions = button(`Retry ${failedCount} failed`, onRetryFailed, true);
      } else {
        icon = <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />;
        tone = classes["RedoCardSuccess"];
        title = `Redid all ${plural(total, "line")} of ${name}`;
      }
      break;

    case "cancelled":
      title = `Stopped after ${succeeded} of ${plural(total, "line")}`;
      body = "The lines already redone keep their new takes.";
      break;

    case "failed":
    default:
      icon = <ErrorOutlineRoundedIcon sx={{ fontSize: 18 }} />;
      tone = classes["RedoCardWarning"];
      title = job?.error?.title || "Redoing lines failed";
      body = [
        job?.error?.detail,
        succeeded
          ? `${plural(succeeded, "line")} ${
              succeeded === 1 ? "was" : "were"
            } redone before it stopped.`
          : null,
      ]
        .filter(Boolean)
        .join(" ");
      actions = button("Start again", onStartAgain, true);
      break;
  }

  return (
    <div className={`${classes["RedoCard"]} ${tone}`} role="status">
      <span className={classes["RedoIcon"]} aria-hidden="true">
        {icon}
      </span>
      <div className={classes["RedoContent"]}>
        <div className={classes["RedoTitle"]}>{title}</div>
        {body && <div className={classes["RedoBody"]}>{body}</div>}
        {actions && <div className={classes["RedoActions"]}>{actions}</div>}
      </div>
      {canDismiss && (
        <button
          type="button"
          className={classes["RedoDismiss"]}
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      )}
    </div>
  );
};

export default RedoLinesCard;
